import { useState } from "react";
import { Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = "h-11 w-full rounded-lg border border-linen bg-white px-3.5 text-sm outline-none transition-colors focus:border-terra";
const emptyForm = { name: "", label: "", desc: "", image: "" };

const CategoriesTab = () => {
  const { categories, products, refreshCategories } = useShop();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const countFor = (name) => products.filter((p) => p.category === name).length;

  const openForm = (cat) => {
    setEditing(cat || null);
    setForm(cat ? { name: cat.name, label: cat.label || "", desc: cat.desc || "", image: cat.image || "" } : emptyForm);
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${BACKEND_URL}${r.data.url}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Category name is required");
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Category updated everywhere");
      } else {
        await api.post("/categories", form);
        toast.success("Category added — it now appears across the site");
      }
      setOpen(false);
      refreshCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/categories/${deleting.id}`);
      toast.success("Category removed");
      setDeleting(null);
      refreshCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      setDeleting(null);
    }
  };

  return (
    <div data-testid="admin-categories-tab">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-clay/60">{categories.length} categories — shown in the menu, shop filters and homepage tiles</p>
        <button
          data-testid="admin-create-category-button"
          onClick={() => openForm(null)}
          className="flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" /> Add category
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-linen bg-white" data-testid="admin-categories-table">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-linen font-mono text-[10px] uppercase tracking-[0.18em] text-clay/60">
              <th className="px-5 py-3.5">Category</th>
              <th className="px-5 py-3.5">Display name</th>
              <th className="px-5 py-3.5">Description</th>
              <th className="px-5 py-3.5">Products</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-linen/70">
            {categories.map((cat) => (
              <tr key={cat.id} className="transition-colors hover:bg-sand/40">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {cat.image ? (
                      <img src={cat.image} alt="" className="h-11 w-11 rounded-lg object-cover" />
                    ) : (
                      <span className="h-11 w-11 rounded-lg bg-sand" />
                    )}
                    <span className="font-mono text-xs text-clay/70">{cat.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3 font-medium text-espresso">{cat.label}</td>
                <td className="max-w-[220px] px-5 py-3 text-xs text-clay/60">{cat.desc || "—"}</td>
                <td className="px-5 py-3 font-mono text-clay/70">{countFor(cat.name)}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => openForm(cat)} className="rounded-lg p-2 text-clay/60 transition-colors hover:bg-sand hover:text-espresso" aria-label={`Edit ${cat.name}`} data-testid={`category-edit-${cat.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleting(cat)} className="rounded-lg p-2 text-clay/60 transition-colors hover:bg-destructive/10 hover:text-destructive" aria-label={`Delete ${cat.name}`} data-testid={`category-delete-${cat.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-clay/40">Renaming a category updates all its products automatically · a category with products can't be deleted</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-espresso">{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="category-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-clay">Category name *</label>
                <input data-testid="category-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Dinner Sets" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-clay">Display name (menu & tiles)</label>
                <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} className={inputCls} placeholder="Dinner & Serveware" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Short description</label>
              <input value={form.desc} onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))} className={inputCls} placeholder="Plates, bowls & serving platters" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Tile photo</label>
              <div className="flex items-start gap-4">
                {form.image && <img src={form.image} alt="Preview" className="h-24 w-24 rounded-xl border border-linen object-cover" />}
                <div className="flex-1 space-y-2">
                  <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg bg-clay px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-cream transition-colors hover:bg-terra ${uploading ? "pointer-events-none opacity-60" : ""}`} data-testid="category-image-upload">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {uploading ? "Uploading…" : "Upload photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
                  </label>
                  <input value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} className={inputCls} placeholder="…or paste an image URL" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setOpen(false)} className="rounded-full border border-linen px-6 py-2.5 text-sm font-semibold text-clay hover:border-clay">Cancel</button>
              <button data-testid="category-form-save" onClick={save} disabled={uploading} className="rounded-full bg-terra px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark disabled:opacity-40">
                {editing ? "Save changes" : "Add category"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="bg-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-espresso">Remove {deleting?.label}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-clay/70">Categories that still have products can't be deleted — reassign those products first.</p>
          <div className="mt-5 flex justify-end gap-3">
            <button onClick={() => setDeleting(null)} className="rounded-full border border-linen px-5 py-2.5 text-sm font-semibold text-clay hover:border-clay">Keep it</button>
            <button onClick={remove} data-testid="category-delete-confirm" className="rounded-full bg-destructive px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">Remove</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesTab;
